import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { barcodeLabelSize } from "../barcode-label";
import {
  AlignItems,
  BarcodeBarSource,
  BarcodeLabelBlockType,
  BarcodeLabelFontWeight,
  PageOrientation,
  ProductBarcodeAnnotation,
} from "./barcode-label-builder";

const baseSectionSchema = z.object({
  blockId: zExt.objectId(),
  sectionPadding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  sectionColor: z.string().nullish(),
  alignSelf: createEnum(AlignItems, { enumName: "AlignItems" }).nullish(),
});

const fontSectionSchema = baseSectionSchema.extend({
  fontFamily: z.string().nullish(),
  fontSize: z.number().nullish(),
  textColor: z.string().nullish(),
  fontWeight: createEnum(BarcodeLabelFontWeight, {
    enumName: "BarcodeLabelFontWeight",
  }).nullish(),
});

const textSchema = baseSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.TEXT),
  text: z.string(),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
});

const spacerSchema = baseSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.SPACER),
  height: z.number(),
});

const lineSchema = baseSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.LINE),
  color: z.string(),
});

const productBarcodeSchema = baseSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_BARCODE),
  source: createEnum(BarcodeBarSource, { enumName: "BarcodeBarSource" }),
  annotation: createEnum(ProductBarcodeAnnotation, {
    enumName: "ProductBarcodeAnnotation",
  }),
  annotationFontSize: z.number().nullish(),
  annotationFontWeight: createEnum(BarcodeLabelFontWeight, {
    enumName: "BarcodeLabelFontWeight",
  }).nullish(),
  width: z.number().nullish(),
  height: z.number().nullish(),
});

const productSkuSchema = fontSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_SKU),
});

const productTitleSchema = fontSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_TITLE),
  maxLines: z.number().nullish(),
});

const productVendorSchema = fontSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_VENDOR),
});

const productPriceSchema = fontSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_PRICE),
  showCurrencyCode: z.boolean().nullish(),
});

const productImageSchema = baseSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_IMAGE),
  maxHeight: z.number().nullish(),
});

const productAttributeSchema = fontSectionSchema.extend({
  type: z.literal(BarcodeLabelBlockType.PRODUCT_ATTRIBUTE),
  fieldKey: z.string(),
  fieldDisplayName: z.string().nullish(),
  label: z.string().nullish(),
});

export const sectionSchema = z.discriminatedUnion("type", [
  textSchema,
  spacerSchema,
  lineSchema,
  productBarcodeSchema,
  productSkuSchema,
  productTitleSchema,
  productVendorSchema,
  productPriceSchema,
  productImageSchema,
  productAttributeSchema,
]);

export type BarcodeLabelSection = z.infer<typeof sectionSchema>;

export const pageLayoutSchema = z.object({
  size: createEnum(barcodeLabelSize, { enumName: "BarcodeLabelSize" }),
  orientation: createEnum(PageOrientation, { enumName: "PageOrientation" }),
});

export type BarcodeLabelPageLayoutSchema = z.infer<typeof pageLayoutSchema>;

export interface IBarcodeLabelPageLayout extends z.ZodType<BarcodeLabelPageLayoutSchema> {}

export const barcodeLabelTemplateSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  team: zExt.objectId(),
  sections: z.array(sectionSchema),
  pageLayout: pageLayoutSchema as IBarcodeLabelPageLayout,
  managedReturnsEnabled: z.boolean().default(false),
  updatedAt: z.string(),
  deletedAt: z.string().optional(),
});

export const barcodeLabelTemplateSchema: z.ZodType<BarcodeLabelTemplate> =
  barcodeLabelTemplateSchema_;

export interface BarcodeLabelTemplate extends z.infer<
  typeof barcodeLabelTemplateSchema_
> {}
